import { useApiClientBase } from "./apiClient";

/**
 * useBalanceSheetApi is a custom hook that provides API functions
 * for fetching balance sheet data and threshold values.
 * It uses the configured API client and error handler from useApiClientBase.
 *
 * @returns { getBalanceSheetResult, getThresholdValues } - Functions to fetch balance sheet results and threshold values.
 */
export const useBalanceSheetApi = () => {
  const { apiClient, handleError } = useApiClientBase();

  /**
   * Fetches balance sheet data from the backend API.
   *
   * @param data - The request payload containing filter and query parameters for the balance sheet.
   * @returns {Promise<any>} - The response data from the API, or null if an error occurs.
   */
  const getBalanceSheetResult = async (data: any) => {
    try {
      const response = await apiClient.post(
        "/CascadingFilter/balancesheetresponse",
        data
      );
      return response.data;
    } catch (err) {
      return handleError(err, "Fetching balance sheet data failed");
    }
  };

  /**
   * Fetches threshold values for the balance sheet from the backend API.
   *
   * @param params - (Optional) Query parameters to filter threshold values.
   * @returns {Promise<any>} - The response data containing threshold values, or null if an error occurs.
   */
  const getThresholdValues = async (params = {}) => {
    try {
      const response = await apiClient.get("/CascadingFilter/thresholdvalues", {
        params,
      });
      return response.data;
    } catch (err) {
      return handleError(err, "Fetching threshold values failed");
    }
  };

  return { getBalanceSheetResult, getThresholdValues };
};
